import { scaleLinear } from 'd3-scale';

import { thresholdIntervals as thresholdIntervalsClass } from './MeterBarChartThresholdIntervals.sty.js';
import { getTokenComputedStyle } from '../../../../core/utils/getTokenComputedStyle.js';
import { getValidColor } from '../../../../core/utils/getValidColor.js';
import {
  LARGE_CHART_SIZE,
  MEDIUM_CHART_SIZE,
  METER_BAR_SIZE,
  SMALL_CHART_SIZE,
} from '../../../shared/constants.js';
import { useMeterBarLayoutSize } from '../../../shared/context/MeterBarLayoutSizeContext.js';
import type { MeterBarSize } from '../../../shared/types/meter-bars.js';
import {
  METER_BAR_CHART_THRESHOLDS_ARIA_LABEL,
  THRESHOLD_INTERVAL_MIN_LENGTH,
} from '../../constants.js';
import { useMeterBarChartData } from '../../context/MeterBarChartDataContext.js';
import { useMeterBarChartInternalConfig } from '../../context/MeterBarChartInternalConfigContext.js';
import { getThresholdIntervalSizing } from '../../utils/get-threshold-interval-sizing.js';

const BAR_THICKNESS_BY_SIZE: Record<MeterBarSize, number> = {
  size8: SMALL_CHART_SIZE,
  size16: MEDIUM_CHART_SIZE,
  size24: LARGE_CHART_SIZE,
  auto: 0,
};

/**
 * Renders the threshold intervals as colored bar segments below the meter bar.
 * Segment sizing (thickness, gaps, offset) scales with the meter bar thickness.
 */
export const MeterBarChartThresholdIntervals = () => {
  const { thresholdIntervals } = useMeterBarChartData();
  const { min, max, showThresholdIndicators } =
    useMeterBarChartInternalConfig();
  const { segmentWidth, size, segmentHeight } = useMeterBarLayoutSize();

  if (
    !showThresholdIndicators ||
    thresholdIntervals.length === 0 ||
    segmentWidth <= 0
  ) {
    return null;
  }

  const barThickness =
    size === METER_BAR_SIZE.Auto ? segmentHeight : BAR_THICKNESS_BY_SIZE[size];
  const { thickness, gap, offset } = getThresholdIntervalSizing(barThickness);

  const scale = scaleLinear()
    .domain([min, max])
    .range([0, segmentWidth])
    .clamp(true);

  const lastIndex = thresholdIntervals.length - 1;

  return (
    <svg
      className={thresholdIntervalsClass}
      height={thickness}
      style={{ marginTop: offset }}
      role="presentation"
      aria-label={METER_BAR_CHART_THRESHOLDS_ARIA_LABEL}
    >
      {thresholdIntervals.map((interval, index) => {
        const start = scale(interval.start);
        const end = scale(interval.end);
        // Reserve a gap on the right of every segment except the last so the
        // intervals stay separated while the row edges remain flush.
        const availableWidth = end - start - (index < lastIndex ? gap : 0);
        const width = Math.max(availableWidth, THRESHOLD_INTERVAL_MIN_LENGTH);

        return (
          <rect
            key={`interval-${interval.start}-${interval.end}`}
            x={start}
            y={0}
            width={width}
            height={thickness}
            rx={thickness / 2}
            fill={getTokenComputedStyle(getValidColor(interval.color))}
            data-testid="meterbar-threshold-interval"
          />
        );
      })}
    </svg>
  );
};

MeterBarChartThresholdIntervals['displayName'] =
  'MeterBarChartThresholdIntervals';
